import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  List,
  ListItem,
  Fade,
  Box,
  Grid,
  Chip,
  ListItemText,
  ListItemSecondaryAction,
} from '@material-ui/core'
import clsx from 'clsx'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import memoize from 'memoize-one'
import moment from 'moment'
import get from 'lodash/get'

import MoreVertIcon from '@material-ui/icons/MoreVert'
import { faArrowsAltV } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { isValidCollation } from 'actions/tours/helpers'
import { getLastStopTime, isPastDeadLine } from 'utils'
import { contrastColor } from 'common/utils/Color'
import { ShipmentTour, Tour } from 'common/models/typings/Tour'
import { Collation } from 'common/models/typings/Collation'

import Typography from 'common/components/base/Typography'

import styles from './styles.module.scss'
import useStyles from '../useStyles'
import { NETWORK_TYPE } from '../../../../models/typings/TourOverview'
import { useSelector } from 'react-redux'
import * as fromNetworkType from '../../../../../selectors/networkType'

interface HandleProps {
  className?: string
  listeners?: any
  attributes?: any
}

const HandleComponent: React.FC<HandleProps> = ({
  className,
  listeners,
  attributes,
}) => (
  <div
    className={clsx(styles.dragHandle, className)}
    {...listeners}
    {...attributes}
  >
    <FontAwesomeIcon icon={faArrowsAltV} />
  </div>
)

interface SortableItemElementProps {
  id: string
  locked: boolean
  collation: Collation
  tour: ShipmentTour
  tours: ShipmentTour[]
  onMoveTour: (
    orgTour: ShipmentTour,
    collation: Collation,
    item: ShipmentTour,
  ) => void
}

export interface SortableListProps {
  locked: boolean
  tour: ShipmentTour
  tours: ShipmentTour[]
  onMoveTour: (
    orgTour: ShipmentTour,
    collation: Collation,
    item: ShipmentTour,
  ) => void
  onSortEnd: (event: { oldIndex: number; newIndex: number }) => void
}

const SortableItemElement = React.memo<SortableItemElementProps>(
  ({ id, collation, tour, tours, onMoveTour, locked }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id,
      disabled: locked,
    })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }
    const { t } = useTranslation()
    const classes = useStyles()
    const {
      estimation,
      position,
      recipient: { location },
      isMultiColli,
      shipmentIds,
    } = collation

    const networkType: NETWORK_TYPE = useSelector(
      fromNetworkType.getNetworkType,
    )

    const [active, setActive] = React.useState(false)
    const [open, setOpen] = React.useState(false)

    const getStartTime = React.useCallback(
      (collations: Collation[]) =>
        moment(get(collations, '[0].estimation')).format('HH:mm'),
      [],
    )

    const getEndDetails = React.useCallback(
      (collations: Collation[]) => {
        return {
          endTime: moment(getLastStopTime(collations)).format('HH:mm'),
          pastDeadline: isPastDeadLine(collations, networkType),
        }
      },
      [networkType],
    )

    const getMenuItems = memoize((accu: ShipmentTour[], orgTour) => {
      return accu.map((item) => {
        return (
          item.id !== tour.id && (
            <ListItem
              button
              disableGutters
              key={item.id}
              onClick={() => onMoveTour(orgTour, collation, item)}
              disabled={item.isWorking}
              className={styles.itemRoot}
            >
              <Box
                className={classes.listItemMarker}
                style={{
                  background: item.color,
                  color: contrastColor(item.color),
                }}
              >
                {item.number}
              </Box>
              <ListItemText
                primary={
                  <>
                    <Typography variant="packageText" color="indigo">
                      {getStartTime(item.collations)}
                      {' - '}
                    </Typography>
                    <Typography variant="packageText" color="indigo">
                      {getEndDetails(item.collations).endTime}
                    </Typography>
                  </>
                }
                style={{ marginLeft: 32 }}
              />
              <ListItemSecondaryAction>
                <Typography variant="packageText" color="indigo">
                  {item.collations
                    .filter(isValidCollation as any)
                    .reduce((a, b) => a + b.shipmentIds.length || a, 0)}
                </Typography>
              </ListItemSecondaryAction>
            </ListItem>
          )
        )
      })
    })

    let menuItems
    if (!locked) {
      menuItems = getMenuItems(tours, tour)
    }

    const mouseEnter = () => {
      setActive(true)
    }

    const mouseLeave = () => {
      setActive(false)
    }

    const handleClick = () => {
      setOpen(!open)
    }

    return (
      <div ref={setNodeRef} style={style}>
        <Box
          className={clsx(
            styles.menu,
            collation.isBermudaStop && styles.bermudaIndicator,
          )}
        >
          <div onMouseEnter={mouseEnter} onMouseLeave={mouseLeave}>
            <Grid container>
              <Grid container item>
                <Grid item xs={10} lg={11}>
                  {active ? (
                    <HandleComponent
                      className={clsx(
                        { [styles.hover]: active },
                        collation.isBermudaStop && styles.dragHandleButton,
                      )}
                      listeners={listeners}
                      attributes={attributes}
                    />
                  ) : (
                    <div
                      className={clsx(
                        styles.dragHandle,
                        collation.isBermudaStop && styles.dragHandleButton,
                      )}
                    >
                      {position}
                    </div>
                  )}
                  <div className={styles.customerInfo}>
                    <span className={styles.address}>{location.address}</span>
                    <span>
                      {location.postalCode} {location.city}
                    </span>
                  </div>
                </Grid>
                <Grid item xs={2} lg={1} className={styles['pull-right']}>
                  <span className={styles.estimation}>
                    {!!estimation && moment(estimation).format('HH:mm')}
                  </span>
                  <span className={styles['more-icon-container']}>
                    {!locked && tours.length >= 2 && (
                      <MoreVertIcon
                        className={styles.moreIcon}
                        onClick={handleClick}
                      />
                    )}
                  </span>
                </Grid>
              </Grid>
              {isMultiColli && (
                <div className={styles.barPositioning}>
                  <Chip
                    label={`${shipmentIds.length} ${t('AMOUNT_OF_PACKAGES')}`}
                  />
                </div>
              )}
            </Grid>
            <Fade style={{ display: open ? 'block' : 'none' }} in={open}>
              <List
                classes={{
                  root: styles.listRoot,
                }}
              >
                <span className={styles.listTitle}>{t('MOVE_TOUR')}</span>
                <Box className={styles.boxScroll}>
                  <List disablePadding className={styles.boxRoot}>
                    {menuItems}
                  </List>
                </Box>
              </List>
            </Fade>
          </div>
        </Box>
      </div>
    )
  },
)

const SortableList: React.FC<SortableListProps> = ({
  tour,
  tours,
  onMoveTour,
  locked,
  onSortEnd,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const validCollations = React.useMemo(
    () => tour.collations.filter(isValidCollation as any),
    [tour.collations],
  )

  const items = React.useMemo(
    () => validCollations.map((stop) => String(stop.id)),
    [validCollations],
  )

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (over && active.id !== over.id) {
        const oldIndex = items.indexOf(String(active.id))
        const newIndex = items.indexOf(String(over.id))

        if (oldIndex !== -1 && newIndex !== -1) {
          onSortEnd({ oldIndex, newIndex })
        }
      }
    },
    [items, onSortEnd],
  )

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <>
          {validCollations.map((stop) => (
            <SortableItemElement
              key={stop.id}
              id={String(stop.id)}
              locked={locked}
              tour={tour}
              collation={stop}
              tours={tours}
              onMoveTour={onMoveTour}
            />
          ))}
        </>
      </SortableContext>
    </DndContext>
  )
}

export default SortableList
